import { Form } from "@inertiajs/react"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import InputError from "@/components/input-error"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useOnline } from "@/hooks/use-online"

/**
 * OKS-26 — Engagement-rate editor (UX Spec V2 §Engagement rate card).
 *
 * Manual numeric entry. 0..100 percent, two decimals.
 * BE strong-params: `params.require(:membership).permit(:notes,
 * :engagement_rate, tags: [])` (the model exposes an `engagement_rate`
 * alias to the `engagement_rate_percent` column).
 *
 * **Crew form rules respected.** Inertia `<Form>` + plain shadcn
 * `Input` + `Label`. No shadcn Form / no RHF / no zod.
 *
 * **Wire-format canary** (Vitest test asserts literal `name` attr):
 * input `name="membership[engagement_rate]"`. Top-level
 * `engagement_rate` would trip `params.require(:membership)` →
 * silent failure.
 *
 * Save button surfaces ONLY when dirty (UX V2 lock — keeps the right
 * column quiet when the value is stable).
 *
 * Blank input (`""`) clears the column on save (BE coerces blank →
 * `nil` via the alias writer).
 *
 * Read-only mode (`!can_edit`): renders the rate as muted text in a
 * card; no form, no inputs.
 *
 * Locked testids:
 *   - `creator-detail-engagement-section` — Card wrapper
 *   - `creator-detail-engagement-form` — `<form>` element
 *   - `creator-detail-engagement-input` — numeric Input
 *   - `creator-detail-engagement-submit` — Save button (visible only when dirty)
 *   - `creator-detail-engagement-error` — Error region
 */
interface EngagementRateEditorProps {
  /** Current server value (0..100, two decimals); null when unset. */
  engagementRate: number | null
  /** BE-supplied PATCH target — `actions.update_url`. */
  updateUrl: string
  canEdit: boolean
}

/**
 * Format a numeric rate for display in the input (`23.45`) or the
 * read-only branch (`23.45 %`). Keeps trailing zeros dropped.
 */
function formatRate(rate: number | null): string {
  if (rate === null) return ""
  // Trim trailing zeros — `23.40` → `23.4`, `25.00` → `25`.
  const fixed = rate.toFixed(2)
  return fixed.replace(/\.?0+$/, "")
}

export function EngagementRateEditor({
  engagementRate,
  updateUrl,
  canEdit,
}: EngagementRateEditorProps) {
  const { t } = useTranslation()
  const online = useOnline()
  const serverValue = formatRate(engagementRate)
  const [value, setValue] = useState(serverValue)
  const dirty = value !== serverValue

  return (
    <Card data-testid="creator-detail-engagement-section">
      <CardHeader>
        <CardTitle>{t("crew.detail.engagement.section_title")}</CardTitle>
      </CardHeader>
      <CardContent>
        {!canEdit && (
          <p className="text-muted-foreground text-sm">
            {engagementRate === null
              ? "—"
              : t("crew.detail.engagement.readonly_value", {
                  value: formatRate(engagementRate),
                })}
          </p>
        )}
        {canEdit && (
          <Form
            action={updateUrl}
            method="patch"
            data-testid="creator-detail-engagement-form"
            options={{ preserveScroll: true }}
          >
            {({ errors, processing }) => (
              <div className="flex flex-col gap-3">
                <Label htmlFor="creator-engagement-input">
                  {t("crew.detail.engagement.label")}
                </Label>
                <div className="relative">
                  <Input
                    id="creator-engagement-input"
                    // **Wire-format canary** — `membership[engagement_rate]`.
                    name="membership[engagement_rate]"
                    type="number"
                    inputMode="decimal"
                    step="0.01"
                    min={0}
                    max={100}
                    value={value}
                    onChange={(e) => setValue(e.target.value)}
                    placeholder="—"
                    disabled={processing}
                    aria-describedby="creator-engagement-helper creator-engagement-error"
                    data-testid="creator-detail-engagement-input"
                    className="pr-10"
                  />
                  <span
                    aria-label={t("crew.detail.engagement.units_aria")}
                    className="text-muted-foreground pointer-events-none absolute top-1/2 right-3 -translate-y-1/2 text-sm"
                  >
                    %
                  </span>
                </div>
                <p
                  id="creator-engagement-helper"
                  className="text-muted-foreground text-xs"
                >
                  {t("crew.detail.engagement.helper")}
                </p>
                <div
                  id="creator-engagement-error"
                  data-testid="creator-detail-engagement-error"
                  role="alert"
                >
                  <InputError messages={errors["membership.engagement_rate"]} />
                </div>
                {dirty && (
                  <div className="flex justify-end">
                    <Button
                      type="submit"
                      disabled={processing || !online}
                      data-testid="creator-detail-engagement-submit"
                    >
                      {processing
                        ? t("crew.detail.engagement.saving")
                        : t("crew.detail.engagement.save")}
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Form>
        )}
      </CardContent>
    </Card>
  )
}
